import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Eye, FileText, Paperclip, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { AppModal } from '../../../components/ui/app-modal';
import { Button } from '../../../components/ui/button';
import { usePermissions } from '../../../hooks/use-permissions';
import { api } from '../../../lib/ky';
import { cn } from '../../../lib/utils';
import { AttachmentPreviewModal, type PreviewAttachment } from './shared-visit-ui';

// ── Types ──────────────────────────────────────────────────────────────────────

interface DocumentType {
  id: string;
  name: string;
  code: string;
}

interface StudyDocument {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  uploadedAt: string;
  signedUrl: string;
  documentType: { id: string; name: string; code: string };
  uploadedBy: { id: string; name: string };
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmt(iso: string) {
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

function mimeIcon(mime: string) {
  if (mime === 'application/pdf') return '📄';
  if (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
    return '📝';
  if (mime.startsWith('image/')) return '🖼️';
  return '📎';
}

// ── Upload Modal ───────────────────────────────────────────────────────────────

function UploadModal({
  open,
  onClose,
  onSubmit,
  isPending,
  docTypes,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (file: File, documentTypeId: string) => void;
  isPending: boolean;
  docTypes: DocumentType[];
}) {
  const [file, setFile] = useState<File | null>(null);
  const [documentTypeId, setDocumentTypeId] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const ALLOWED_EXTS = '.pdf,.docx,.jpg,.jpeg,.png';
  const MAX_MB = 10;

  function handleClose() {
    setFile(null);
    setDocumentTypeId('');
    onClose();
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !documentTypeId) return;
    onSubmit(file, documentTypeId);
  }

  const sizeOk = file ? file.size <= MAX_MB * 1024 * 1024 : true;
  const isValid = !!file && !!documentTypeId && sizeOk;

  return (
    <AppModal open={open} onClose={handleClose} title="Upload Municipality Document">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-700">
            Document Type <span className="text-error-500">*</span>
          </label>
          <select
            value={documentTypeId}
            onChange={(e) => setDocumentTypeId(e.target.value)}
            required
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          >
            <option value="">Select document type…</option>
            {docTypes.map((dt) => (
              <option key={dt.id} value={dt.id}>
                {dt.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-700">
            File <span className="text-error-500">*</span>
          </label>
          <div
            onClick={() => inputRef.current?.click()}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors',
              file
                ? 'border-brand-300 bg-brand-50'
                : 'border-gray-200 bg-gray-50 hover:border-brand-300 hover:bg-brand-50/30',
            )}
          >
            <Paperclip
              size={22}
              className={cn('mb-2', file ? 'text-brand-500' : 'text-gray-300')}
            />
            {file ? (
              <>
                <p className="text-xs font-semibold text-brand-600 truncate max-w-[220px]">
                  {file.name}
                </p>
                <p
                  className={cn('mt-0.5 text-[10px]', sizeOk ? 'text-gray-400' : 'text-error-500')}
                >
                  {formatBytes(file.size)}
                  {!sizeOk && ' — exceeds 10 MB limit'}
                </p>
              </>
            ) : (
              <>
                <p className="text-xs font-medium text-gray-500">Click to select a file</p>
                <p className="mt-0.5 text-[10px] text-gray-400">PDF, DOCX, JPG, PNG · max 10 MB</p>
              </>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_EXTS}
            onChange={handleFile}
            className="hidden"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isPending || !isValid}>
            {isPending ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      </form>
    </AppModal>
  );
}

// ── Delete confirmation modal ──────────────────────────────────────────────────

function DeleteModal({
  open,
  onClose,
  onConfirm,
  fileName,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  fileName: string;
  isPending: boolean;
}) {
  return (
    <AppModal open={open} onClose={onClose} title="Delete Document">
      <p className="text-sm text-gray-600">
        Are you sure you want to delete{' '}
        <span className="font-semibold text-gray-800">{fileName}</span>? This action cannot be
        undone.
      </p>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={onConfirm}
          disabled={isPending}
          className="bg-error-500 hover:bg-error-600 focus:ring-error-200"
        >
          {isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </div>
    </AppModal>
  );
}

// ── Document row ───────────────────────────────────────────────────────────────

function DocumentRow({
  doc,
  canDelete,
  onDelete,
  onPreview,
}: {
  doc: StudyDocument;
  canDelete: boolean;
  onDelete: () => void;
  onPreview: (attachment: PreviewAttachment) => void;
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <tr className="border-b border-gray-100 transition-colors hover:bg-gray-50/50 last:border-0">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="text-lg leading-none">{mimeIcon(doc.mimeType)}</span>
          <div>
            <p className="text-xs font-semibold text-gray-800 truncate max-w-[200px]">
              {doc.fileName}
            </p>
            <p className="text-[10px] text-gray-400">{formatBytes(doc.fileSizeBytes)}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="rounded-lg bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-600">
          {doc.documentType.name}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-gray-600">{doc.uploadedBy.name}</td>
      <td className="px-4 py-3 text-xs text-gray-500">{fmt(doc.uploadedAt)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <a
            href={doc.signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Download"
            className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-brand-50 hover:text-brand-600 transition-colors"
          >
            <Download size={13} />
          </a>
          <button
            type="button"
            title="Preview"
            onClick={() =>
              onPreview({ fileName: doc.fileName, mimeType: doc.mimeType, url: doc.signedUrl })
            }
            className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-brand-50 hover:text-brand-600 transition-colors"
          >
            <Eye size={13} />
          </button>
          {canDelete && (
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-error-50 hover:text-error-500 transition-colors"
              title="Delete"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </td>

      <DeleteModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => {
          onDelete();
          setDeleteOpen(false);
        }}
        fileName={doc.fileName}
        isPending={false}
      />
    </tr>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function MunicipalityAttachmentsTab({ requestId }: { requestId: string }) {
  const { can } = usePermissions();
  const canUpload = can('sm-study:upload-document');
  const canView = can('sm-study:view-documents');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<PreviewAttachment | null>(null);
  const qc = useQueryClient();

  const docsKey = ['study-documents', requestId];
  const typesKey = ['study-document-types'];

  const {
    data: docs,
    isLoading,
    isError,
  } = useQuery({
    queryKey: docsKey,
    queryFn: () => api.get(`study-requests/${requestId}/documents`).json<StudyDocument[]>(),
    enabled: canView || canUpload,
  });

  const { data: docTypes = [] } = useQuery({
    queryKey: typesKey,
    queryFn: () => api.get(`study-requests/${requestId}/documents/types`).json<DocumentType[]>(),
    enabled: canUpload,
    staleTime: 5 * 60 * 1000,
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ file, documentTypeId }: { file: File; documentTypeId: string }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('documentTypeId', documentTypeId);
      return api.post(`study-requests/${requestId}/documents`, { body: fd }).json();
    },
    onSuccess: () => {
      setUploadOpen(false);
      qc.invalidateQueries({ queryKey: docsKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => api.delete(`study-requests/${requestId}/documents/${docId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: docsKey });
    },
  });

  if (!canView && !canUpload) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-sm">
        <FileText size={32} className="mx-auto mb-3 text-gray-200" />
        <p className="text-sm font-semibold text-gray-500">Access Restricted</p>
        <p className="mt-1 text-xs text-gray-400">
          You do not have permission to view municipality documents.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Documents</h3>
          <p className="text-xs text-gray-400">Documents uploaded for this study request.</p>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="rounded-2xl border border-error-200 bg-error-50 p-6 text-center">
          <p className="text-sm font-medium text-error-600">Failed to load documents</p>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && (docs ?? []).length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center shadow-sm">
          <Paperclip size={32} className="mx-auto mb-3 text-gray-200" />
          <p className="text-sm font-semibold text-gray-500">No documents uploaded yet</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && (docs ?? []).length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    File
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Uploaded By
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {(docs ?? []).map((doc) => (
                  <DocumentRow
                    key={doc.id}
                    doc={doc}
                    canDelete={
                      canUpload &&
                      doc.documentType.code !== 'STUDY_REPORT' &&
                      doc.documentType.code !== 'LAB_REPORT'
                    }
                    onDelete={() => deleteMutation.mutate(doc.id)}
                    onPreview={setPreviewAttachment}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSubmit={(file, documentTypeId) => uploadMutation.mutate({ file, documentTypeId })}
        isPending={uploadMutation.isPending}
        docTypes={docTypes}
      />

      <AttachmentPreviewModal
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />
    </div>
  );
}
